import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ArrowDown, ArrowUp, DotsSixVertical } from 'phosphor-react';
import { useActionToast } from '../../services/actionToast';
import { api } from '../../services/api';
import { DataSkeleton, EmptyState } from '../states';

export interface ModuleMaterial {
  id: number;
  name: string;
  status: boolean;
  position: number;
  category?: { name?: string };
}

interface ModuleMaterialsManagerProps {
  isOpen: boolean;
  moduleId: number | null;
  moduleName: string;
  onClose(): void;
  onCountsChanged(): void;
}

const moveItem = (items: ModuleMaterial[], from: number, to: number) => {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export function ModuleMaterialsManager({
  isOpen,
  moduleId,
  moduleName,
  onClose,
  onCountsChanged,
}: ModuleMaterialsManagerProps) {
  const toast = useActionToast();
  const [materials, setMaterials] = useState<ModuleMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const loadMaterials = useCallback(async () => {
    if (!moduleId) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/v1/admin/extra_materials/modules/${moduleId}/materials`);
      setMaterials(response.data?.data || []);
    } catch {
      setError('Não foi possível carregar os materiais');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (isOpen) {
      loadMaterials();
    }
  }, [isOpen, loadMaterials]);

  const saveOrder = async (next: ModuleMaterial[], previous: ModuleMaterial[]) => {
    if (!moduleId) {
      return;
    }
    setMaterials(next);
    setSaving(true);
    setError('');
    try {
      const response = await api.put(`/v1/admin/extra_materials/modules/${moduleId}/reorder`, {
        material_ids: next.map((item) => item.id),
      });
      setMaterials(response.data?.data || next);
      toast({ title: 'Ordem salva', status: 'success', duration: 2000 });
    } catch {
      setMaterials(previous);
      setError('Não foi possível salvar a ordem');
      toast({
        title: 'Ordem não salva',
        description: 'Não foi possível salvar a ordem dos materiais.',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const changePosition = (from: number, to: number) => {
    if (saving || to < 0 || to >= materials.length || from === to) {
      return;
    }
    saveOrder(moveItem(materials, from, to), materials);
  };

  const handleKeyDown = (event: KeyboardEvent, index: number) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      changePosition(index, index - 1);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      changePosition(index, index + 1);
    }
  };

  const toggleVisibility = async (material: ModuleMaterial) => {
    const previous = materials;
    const nextStatus = !material.status;
    setMaterials((current) =>
      current.map((item) => (item.id === material.id ? { ...item, status: nextStatus } : item))
    );
    setSaving(true);
    setError('');
    try {
      await api.put(
        `/v1/admin/extra_materials/modules/${moduleId}/materials/${material.id}/visibility`,
        { status: nextStatus }
      );
      onCountsChanged();
      toast({
        title: nextStatus ? 'Material publicado' : 'Material ocultado',
        status: 'success',
        duration: 2000,
      });
    } catch {
      setMaterials(previous);
      setError('Não foi possível alterar a visibilidade');
      toast({
        title: 'Visibilidade não atualizada',
        description: 'Não foi possível alterar a visibilidade do material.',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Materiais do módulo: {moduleName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="gray.600" mb={5}>
            Arraste um material ou use as setas do teclado para alterar a ordem.
          </Text>
          {error && (
            <Alert status="error" mb={4} role="alert">
              <AlertIcon />
              {error}
            </Alert>
          )}
          {loading ? (
            <DataSkeleton variant="detail" rows={5} label="Carregando materiais do módulo" />
          ) : (
            <Flex direction="column" gap={3} data-testid="module-material-list">
              {materials.map((material, index) => (
                <Flex
                  key={material.id}
                  align="center"
                  gap={3}
                  borderWidth="1px"
                  borderRadius="md"
                  p={3}
                  background="white"
                  draggable={!saving}
                  onDragStart={(event) => {
                    setDragIndex(index);
                    event.dataTransfer.setData('text/plain', String(material.id));
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragIndex !== null) {
                      changePosition(dragIndex, index);
                    }
                    setDragIndex(null);
                  }}
                  data-testid={`material-row-${material.id}`}
                >
                  <Box
                    as="button"
                    type="button"
                    aria-label={`Reordenar ${material.name}. Use as setas para mover.`}
                    cursor="grab"
                    onKeyDown={(event: KeyboardEvent) => handleKeyDown(event, index)}
                    disabled={saving}
                  >
                    <DotsSixVertical size={26} aria-hidden="true" />
                  </Box>
                  <Badge minW="32px" textAlign="center">
                    {index + 1}
                  </Badge>
                  <Box flex="1">
                    <Text fontWeight="600">{material.name}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {material.category?.name || 'Sem categoria'}
                    </Text>
                  </Box>
                  <Tooltip label="Mover para cima">
                    <IconButton
                      aria-label={`Mover ${material.name} para cima`}
                      icon={<ArrowUp />}
                      size="sm"
                      isDisabled={saving || index === 0}
                      onClick={() => changePosition(index, index - 1)}
                    />
                  </Tooltip>
                  <Tooltip label="Mover para baixo">
                    <IconButton
                      aria-label={`Mover ${material.name} para baixo`}
                      icon={<ArrowDown />}
                      size="sm"
                      isDisabled={saving || index === materials.length - 1}
                      onClick={() => changePosition(index, index + 1)}
                    />
                  </Tooltip>
                  <Flex align="center" gap={2} minW="135px" justify="end">
                    <Text fontSize="sm">{material.status ? 'Público' : 'Oculto'}</Text>
                    <Switch
                      aria-label={`Alterar visibilidade de ${material.name}`}
                      isChecked={material.status}
                      isDisabled={saving}
                      onChange={() => toggleVisibility(material)}
                    />
                  </Flex>
                </Flex>
              ))}
              {!error && !materials.length && (
                <EmptyState
                  title="Nenhum material neste módulo"
                  description="Adicione materiais ao módulo para organizar a ordem e a visibilidade."
                />
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          {saving && <Spinner size="sm" mr={3} aria-label="Salvando alterações" />}
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
